import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { exportCSVFile, exportExcelFile, isObject } from '@helpers';
import {
  Stack,
  OutlinedInput,
  InputAdornment,
  SearchIcon,
  Button,
  TuneIcon,
  Menu,
  Badge,
  MenuItem,
  Typography,
  FileDownloadOutlinedIcon
} from '@esgian/esgianui';
import TableFiltersModal from '@components/Tables/TableFiltersModal';

const TableActions = React.memo(
  ({
    handleExportClick,
    setTableFilters,
    setDebouncedSearchQuery,
    tableFilters = {},
    filterCount = 0,
    loading = false,
    columns = [],
    originalData = [],
    handleCustomDownload = undefined,
    hideFilter = false
  }) => {
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [anchorEl, setAnchorEl] = useState(null);
    const [buttonSize, setButtonSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearchQuery(searchQuery);
      }, 500);
      return () => clearTimeout(timer);
    }, [searchQuery]);

    return (
      <Stack direction={'row'}>
        <OutlinedInput
          disabled={loading}
          fullWidth
          sx={{
            borderTopLeftRadius: '10px',
            fontSize: (theme) => theme.typography.body2.fontSize, // Apply body2 font size
            height: 30, // Set desired height
            borderTopRightRadius: '0px',
            borderBottomRightRadius: '0px',
            borderBottomLeftRadius: '10px'
          }}
          onChange={({ target }) => setSearchQuery(target.value)}
          value={searchQuery}
          inputProps={{ style: { padding: '8px' } }}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  width: '0.75em !important',
                  height: '0.75em !important'
                }}
              />
            </InputAdornment>
          }
          variant={'outlined'}
          placeholder={'Search...'}
        />

        <Stack direction={'row'} sx={{ height: '30px' }}>
          {!hideFilter && (
            <Button
              color={'secondary'}
              size={'small'}
              variant={'outlined'}
              onClick={() => setFilterModalOpen(true)}
              sx={{
                color: ({ palette }) => palette.text.primary,
                borderColor: ({ palette }) => (palette.mode === 'light' ? '#bdbdbd' : '#3b3b3b'),
                borderRight: '0px',
                minWidth: '4em',
                borderLeft: '0px',
                '&:hover': {
                  borderRight: '0px',
                  borderLeft: '0px'
                },
                borderRadius: '0px !important'
              }}>
              <Badge
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                badgeContent={filterCount}
                color="info">
                <TuneIcon
                  sx={{
                    width: '0.75em !important',
                    height: '0.75em !important'
                  }}
                  color={'inherit'}
                />
              </Badge>
            </Button>
          )}
          <Button
            color={'secondary'}
            size={'small'}
            startIcon={
              <FileDownloadOutlinedIcon
                sx={{
                  width: '0.85em !important',
                  height: '0.85em !important'
                }}
                color={'inherit'}
              />
            }
            onClick={(event) => {
              if (handleCustomDownload) {
                handleCustomDownload();
                return;
              }
              setAnchorEl(event.currentTarget);
              // Get button size directly from the event
              const { width, height } = event.currentTarget.getBoundingClientRect();
              setButtonSize({ width, height });
            }}
            variant={'outlined'}
            sx={{
              color: ({ palette }) => palette.text.primary,
              whiteSpace: 'nowrap',
              borderColor: ({ palette }) => (palette.mode === 'light' ? '#bdbdbd' : '#3b3b3b'),
              borderRadius: '0px !important'
            }}>
            export
          </Button>
          <Menu
            disableScrollLock
            PaperProps={{
              sx: {
                width: buttonSize.width, // Set menu width to the button width
                minWidth: 0 // Optional: To avoid the menu being wider than the button
              }
            }}
            id="basic-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}>
            <MenuItem onClick={() => handleExportClick('CSV')}>
              <Typography variant={'body2'}>CSV</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleExportClick('Excel')}>
              <Typography variant={'body2'}>Excel</Typography>
            </MenuItem>
          </Menu>
        </Stack>
        {filterModalOpen && (
          <TableFiltersModal
            handleSave={(filters) => {
              setFilterModalOpen(false);
              setTableFilters(filters);
            }}
            tableFilters={tableFilters}
            columns={columns}
            data={originalData}
            handleClose={() => setFilterModalOpen(false)}
          />
        )}
      </Stack>
    );
  }
);

TableActions.propTypes = {
  handleExportClick: PropTypes.func.isRequired,
  setDebouncedSearchQuery: PropTypes.func.isRequired,
  setTableFilters: PropTypes.func.isRequired,
  tableFilters: PropTypes.object,
  filterCount: PropTypes.number,
  handleCustomDownload: PropTypes.func,
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.object),
  originalData: PropTypes.arrayOf(PropTypes.object),
  hideFilter: PropTypes.bool
};
TableActions.displayName = 'TableActions';

export default TableActions;
